// @ts-nocheck

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Heart, Search, Gavel, Clock, Eye, X, Plus } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface SavedLot {
  id: string;
  lotNumber: string;
  auctionTitle: string;
  artworkTitle: string;
  artworkImage: string;
  startingBid: number;
  currentBid: number;
  estimatedValue: { low: number; high: number };
  endTime: string;
  status: 'active' | 'upcoming' | 'ended';
  savedAt: string;
}

const SavedLotsTab: React.FC = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  const { data: savedLots = [], isLoading } = useQuery({
    queryKey: ['userSavedLots', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Try RPC first
      const { data, error } = await supabase.rpc('get_user_saved_lots', {
        p_user_id: user.id
      });

      if (!error && Array.isArray(data)) {
        return data;
      }

      // Fallback to direct join query if RPC fails (e.g., not yet migrated)
      console.warn('SavedLotsTab: RPC get_user_saved_lots failed, falling back to direct query', error);
      const { data: fallback, error: fbError } = await supabase
        .from('saved_lots')
        .select(`
          saved_lot_id:id,
          lot_id,
          saved_at:created_at,
          lot:lots(
            id,
            lot_number,
            starting_bid,
            current_bid,
            auction:auction_id(
              id,
              title,
              start_date,
              status
            ),
            artwork:artwork_id(
              title,
              image_url
            )
          )
        `)
        .eq('user_id', user.id as any)
        .order('created_at', { ascending: false });

      if (fbError) {
        console.error('SavedLotsTab: Fallback query failed:', fbError);
        return [];
      }

      return (fallback || []).map((row: any) => ({
        saved_lot_id: row.saved_lot_id ?? row.id,
        lot_id: row.lot_id ?? row.lot?.id,
        lot_number: row.lot?.lot_number ?? null,
        auction_id: row.lot?.auction?.id ?? null,
        auction_title: row.lot?.auction?.title ?? null,
        auction_start_date: row.lot?.auction?.start_date ?? null,
        auction_status: row.lot?.auction?.status ?? null,
        artwork_title: row.lot?.artwork?.title ?? null,
        artwork_image_url: row.lot?.artwork?.image_url ?? null,
        starting_bid: row.lot?.starting_bid ?? null,
        current_bid: row.lot?.current_bid ?? null,
        saved_at: row.saved_at ?? row.created_at,
      }));
    },
    enabled: !!user?.id,
  });

  const removeSavedLotMutation = useMutation({
    mutationFn: async (lotId: string) => {
      const { error } = await supabase
        .from('saved_lots')
        .delete()
        .eq('lot_id', lotId)
        .eq('user_id', user?.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSavedLots', user?.id] });
      toast({
        title: "Lot removed",
        description: "The lot has been removed from your saved lots.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove the lot. Please try again.",
        variant: "destructive",
      });
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ended': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredLots = Array.isArray(savedLots) ? savedLots.filter((lot: any) =>
    (lot.artwork_title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    lot.lot_number.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
    (lot.auction_title || '').toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  const removeSavedLot = (lotId: string) => {
    removeSavedLotMutation.mutate(lotId);
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-8 space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <h2 className="text-2xl font-light text-white mb-2">Saved Lots</h2>
            <p className="text-white/70 font-light">Keep track of auction lots you're interested in</p>
          </div>
          
          {/* Search */}
          <div className="relative w-full sm:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              placeholder="Search saved lots..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/10 backdrop-blur-xl border-white/20 text-white placeholder:text-white/40 focus:border-white/40 rounded-xl"
            />
          </div>
        </motion.div>

        {/* Content */}
        {isLoading ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
              <CardContent className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
                <p className="text-white/70 font-light">Loading your saved lots...</p>
              </CardContent>
            </Card>
          </motion.div>
        ) : filteredLots.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-white/5 backdrop-blur-xl border-white/20 border-dashed shadow-xl">
              <CardContent className="p-12 text-center">
                {searchQuery ? (
                  <>
                    <Search className="h-16 w-16 text-white/40 mx-auto mb-6" />
                    <h3 className="text-xl font-light text-white mb-2">No lots found</h3>
                    <p className="text-white/60 font-light">Try adjusting your search terms</p>
                  </>
                ) : (
                  <>
                    <Heart className="h-16 w-16 text-white/40 mx-auto mb-6" />
                    <h3 className="text-xl font-light text-white mb-2">No saved lots yet</h3>
                    <p className="text-white/60 font-light mb-6">Save lots you're interested in to keep track of them</p>
                    <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg hover:shadow-xl transform hover:scale-105">
                      <Plus className="h-4 w-4 mr-2" />
                      Browse Auctions
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {filteredLots.map((lot: any, index: number) => (
              <motion.div
                key={lot.saved_lot_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
                  <CardContent className="p-8">
                    <div className="flex gap-6">
                      {/* Artwork Image */}
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <img 
                            src={lot.artwork_image_url || 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400'} 
                            alt={lot.artwork_title || 'Artwork'}
                            className="w-32 h-32 object-cover rounded-2xl border border-white/20 shadow-lg"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
                        </div>
                      </div>

                      {/* Lot Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-4">
                          <div className="space-y-2">
                            <h3 className="text-xl font-medium text-white">{lot.artwork_title || 'Untitled'}</h3>
                            <p className="text-white/70 font-light">{lot.auction_title}</p>
                            <p className="text-sm text-white/50 uppercase tracking-wider font-medium">
                              LOT #{lot.lot_number}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={cn(
                              "px-3 py-1 rounded-xl font-medium",
                              (lot.auction_status === 'active' || lot.auction_status === 'live') 
                                ? "bg-emerald-500/20 text-emerald-400 border-emerald-400/30" 
                                : "bg-blue-500/20 text-blue-400 border-blue-400/30"
                            )}>
                              {(lot.auction_status === 'active' || lot.auction_status === 'live') && <Clock className="h-3 w-3 mr-1" />}
                              {lot.auction_status || 'upcoming'}
                            </Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeSavedLot(lot.lot_id)}
                              disabled={removeSavedLotMutation.isPending}
                              className="text-white/60 hover:text-red-400 hover:bg-red-500/10 p-2 rounded-xl"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-6 mb-6">
                          <div>
                            <p className="text-xs text-white/50 uppercase tracking-wider font-medium mb-1">Starting Bid</p>
                            <p className="text-lg font-bold text-white">{formatCurrency(lot.starting_bid)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-white/50 uppercase tracking-wider font-medium mb-1">Current Bid</p>
                            <p className="text-lg font-bold text-white">
                              {lot.current_bid && lot.current_bid > 0 ? formatCurrency(lot.current_bid) : 'No bids yet'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-white/50 uppercase tracking-wider font-medium mb-1">Auction Date</p>
                            <p className="text-lg font-bold text-white">
                              {new Date(lot.auction_start_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-6 border-t border-white/10">
                          <div className="space-y-1">
                            <p className="text-xs text-white/50 flex items-center gap-2">
                              <Heart className="h-3 w-3" />
                              Saved: {new Date(lot.saved_at).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-white/50">
                              Status: {lot.auction_status}
                            </p>
                          </div>
                          <div className="flex gap-3">
                            <Button size="sm" variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20 backdrop-blur-sm rounded-xl">
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </Button>
                            {(lot.auction_status === 'active' || lot.auction_status === 'live') && (
                              <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg hover:shadow-xl rounded-xl">
                                <Gavel className="h-4 w-4 mr-2" />
                                Place Bid
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

export default SavedLotsTab;